import React, { useEffect, useRef, useState } from 'react';
import type { DiagnosticExamType } from '@/utils/diagnosticMemory';
import { trackAimShown } from '@/utils/signupRoad';

export interface AimAnswers {
  exam: DiagnosticExamType;
  /** YYYY-MM-DD, or null when she said she has no date yet. */
  date: string | null;
  retake: boolean;
}

interface AimScreenProps {
  exams: readonly DiagnosticExamType[];
  initialExam: DiagnosticExamType;
  initialRetake?: boolean;
  submitting: boolean;
  error?: string;
  onSubmit: (answers: AimAnswers) => void;
}

/** Local YYYY-MM-DD (en-CA formats that way), so a US evening can still pick today. */
const localIso = (d: Date) => d.toLocaleDateString('en-CA');
const yearsOut = (n: number) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + n);
  return d;
};

/** The one aim screen a signed-in six-turn finisher sees before day 1 (meta #321 P2-3). */
const AimScreen: React.FC<AimScreenProps> = ({ exams, initialExam, initialRetake = false, submitting, error, onSubmit }) => {
  const [exam, setExam] = useState<DiagnosticExamType>(initialExam);
  const [date, setDate] = useState('');
  const [noDate, setNoDate] = useState(false);
  const [retake, setRetake] = useState(initialRetake);
  const canSubmit = (noDate || !!date) && !submitting;
  const shownTracked = useRef(false);
  useEffect(() => {
    if (shownTracked.current) return;
    shownTracked.current = true;
    trackAimShown(true);
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50 px-4 py-8 sm:py-12">
      <form
        className="card mx-auto w-full max-w-xl sm:p-10"
        data-testid="onboarding-aim"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit({ exam, date: noDate ? null : date, retake });
        }}
      >
        <p className="eyebrow mb-3 tracking-widest text-primary-600">One last step</p>
        <h1 className="heading-1 md:text-5xl">What are you aiming for?</h1>
        <p className="body-large mt-3 text-neutral-600">Two quick answers so day 1 fits you.</p>

        {error && (
          <p role="alert" className="mt-4 rounded-xl border border-error/30 bg-error-light p-3 text-sm text-error">
            {error}
          </p>
        )}

        <h2 className="heading-3 mt-6 font-bold">Which exam?</h2>
        <div className="mt-4 grid grid-cols-3 gap-3" role="radiogroup" aria-label="Which exam?">
          {exams.map((choice) => {
            const selected = exam === choice;
            return (
              <button
                key={choice}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid={`aim-exam-${choice}`}
                onClick={() => setExam(choice)}
                className={`rounded-2xl border-2 py-3.5 text-lg font-bold transition-colors ${
                  selected
                    ? 'border-primary-700 bg-primary-500 text-white shadow-ledge-brand'
                    : 'border-neutral-200 bg-white text-neutral-900 shadow-ledge-sm hover:border-primary-300'
                }`}
              >
                {choice}
              </button>
            );
          })}
        </div>

        <h2 className="heading-3 mt-7 font-bold">When are you testing?</h2>
        <p className="body-small mt-3">
          Pick a date if you have one. If you don&apos;t yet, say so. We never guess one for you.
        </p>
        <input
          type="date"
          aria-label="Pick your exam date"
          placeholder="Pick your exam date"
          data-testid="aim-date"
          min={localIso(new Date())}
          max={localIso(yearsOut(3))}
          value={noDate ? '' : date}
          disabled={noDate}
          onChange={(e) => setDate(e.target.value)}
          className="mt-4 w-full rounded-2xl border-2 border-neutral-200 bg-neutral-50 px-4 py-3.5 text-lg text-neutral-900 transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50"
        />
        <label className="mt-4 flex cursor-pointer items-center gap-3 text-lg font-semibold text-neutral-900">
          <input
            type="checkbox"
            data-testid="aim-no-date"
            checked={noDate}
            onChange={(e) => setNoDate(e.target.checked)}
            className="h-6 w-6 rounded-md border-2 border-neutral-200 accent-primary-500"
          />
          I don&apos;t have a date yet
        </label>

        <div className="mt-5 flex items-center justify-between gap-4 rounded-2xl bg-celebrate-100 p-5">
          <div>
            <p id="aim-retake-label" className="text-lg font-bold text-neutral-900">
              This is a retake
            </p>
            <p className="body-small">I have sat for this exam before. Start where it hurt.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={retake}
            aria-labelledby="aim-retake-label"
            data-testid="aim-retake"
            onClick={() => setRetake((r) => !r)}
            className={`relative h-9 w-16 shrink-0 rounded-full transition-colors ${
              retake ? 'bg-primary-500' : 'bg-neutral-200'
            }`}
          >
            <span
              className={`absolute top-1 h-7 w-7 rounded-full bg-white shadow-ledge-sm transition-transform ${
                retake ? 'translate-x-8' : 'translate-x-1'
              } left-0`}
            />
          </button>
        </div>

        <button
          type="submit"
          data-testid="aim-submit"
          disabled={!canSubmit}
          className="btn-primary mt-6 w-full py-4 text-lg shadow-ledge-brand"
        >
          Start day 1
        </button>
        <p className="mt-4 text-center text-sm text-neutral-500">
          Your first readiness number comes after your first set of questions.
        </p>
      </form>
    </div>
  );
};

export default AimScreen;
